<template>
    <div v-if="!loading && operations" class="w-full flex flex-column m-0 p-0">

        <div class="flex gap-2 mx-3">
            <Button label="Сбросить" @click="resetFilter()" class="border-round-md border-0 text-xs font-medium"
                icon="pi pi-refresh" iconPos="right" severity="secondary" size="small"/>
        </div>

        <DataTable v-if="operations" :value="operations" lazy paginator :first="lazyParams.first" :rows="lazyParams.rows" v-model:filters="lazyParams.filters" ref="dt" dataKey="id"
            :totalRecords="totalRecords" :loading="loading" @page="onPage($event)" @sort="onSort($event)"
            @filter="onFilter($event)" filterDisplay="menu" 
            @row-select="onRowSelect" @row-unselect="onRowUnselect" 
            :rowsPerPageOptions="[5, 10, 20, 50, 100, 200, 300, 400, 500]">

            <Column field="_id" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true">
                <template #header>
                    <span class="text-xs font-medium">ID</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
                </template>
                <template #body="{ data }">
                    <div class="flex flex-row gap-1 text-xs vertical-align-middle justify-content-start align-items-center">
                        <span class="flex my-auto overflow-hidden" style="max-width:5em">
                            <span class="white-space-nowrap" style="text-overflow:ellipsis;">
                                {{ data.id }}
                            </span>
                        </span>...
                        <Button @click="copy(data.id)" icon="pi pi-copy" iconPos="right" size="small" outlined severity="secondary" class="border-0" />
                    </div> 
                </template>
            </Column>

            <Column field="status" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" :showApplyButton="false">
                <template #header>
                    <span class="text-xs font-medium">status</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <Dropdown v-model="filterModel.value" @change="filterCallback()" :options="statuses" placeholder="Все" class="p-column-filter" :showClear="false">
                        <template #option="slotProps">
                            <Tag :severity="getSeverityByStatus(slotProps.option)" :value="slotProps.option" class="text-xs"/>
                        </template>
                    </Dropdown>
                </template>
                <template #body="{ data }">
                    <Tag :severity="getSeverityByStatus(data.status)" :value="data.status" class="text-xs"/>
                </template>
            </Column>

            <Column field="step" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" :showApplyButton="false" >
                <template #header>
                    <span class="text-xs font-medium">step</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <Dropdown v-model="filterModel.value" @change="filterCallback()" :options="steps" placeholder="Все" class="p-column-filter" :showClear="false">
                        <template #option="slotProps">
                            <Tag :severity="getSeverityByStatus(slotProps.option)" :value="slotProps.option" class="text-xs"/>
                        </template>
                    </Dropdown>
                </template>
                <template #body="{ data }">
                    <Tag :severity="getSeverityByStatus(data.step)" :value="data.step" class="text-xs"/>
                </template>
            </Column>

            <Column field="type" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" :showApplyButton="false" >
                <template #header>
                    <span class="text-xs font-medium">type</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <Dropdown v-model="filterModel.value" @change="filterCallback()" :options="types" placeholder="Все" class="p-column-filter" :showClear="true">
                        <template #option="slotProps">
                            <Tag :severity="getSeverityByType(slotProps.option)" :value="slotProps.option" class="text-xs"/>
                        </template>
                    </Dropdown>
                </template>
                <template #body="{ data }">
                    <Tag :severity="getSeverityByType(data.type)" :value="data.type" class="text-xs"/>
                </template>
            </Column>

            <Column field="data">
                <template #header>
                    <span class="text-xs font-medium">Проводки</span>
                </template>
                <template #body="{ data }">
                    <div class="flex flex-row">
                    <Button 
                        icon="pi pi-file"
                        :label="data.items ? data.items.length : 0"
                        @click="showTx(data.items)" :disabled="!data.items" 
                        severity="secondary" size="small"
                        class="text-xs flex"/>  
                    </div>
                </template>
            </Column>

            <Column field="data">
                <template #header>
                    <span class="text-xs font-medium">Суммы</span>
                </template>
                <template #body="{ data }">
                    <div v-if="data.items && data.items?.length > 0" class="flex flex-row">
                        <div class="flex flex-row align-items-center gap-1">
                            <AvatarGroup v-if="data.items[0].currency">
                                <Avatar v-if="data.items[0].currency" :image="GetCurrencyImage(data.items[0].currency)" size="small" shape="circle" style="width:25px!important" />
                                <Avatar v-if="data.items[0].network" :image="GetNetworkImage(data.items[0].network)" size="small" shape="circle" style="width:25px!important"/>
                            </AvatarGroup>
                            <span v-if="data.items[0].amount" class="text-sm font-medium white-space-nowrap" v-tooltip="{ value: getFormatAmount(data.items[0].amount, data.items[0].currency, 6) }">
                                {{ getFormatAmount(data.items[0].amount, data.items[0].currency, 2) }}
                            </span>
                        </div>
                    </div>
                </template>
            </Column>

            <Column field="tx">
                <template #header>
                    <span class="text-xs font-medium">Tx</span>
                </template>
                <template #body="{ data }">
                    <Button label="Tx" @click="goToTransactions(data.id)" icon="pi pi-external-link" iconPos="right" size="small" outlined severity="info" class="border-0" />
                </template>
            </Column>

            <Column field="description">
                <template #header>
                    <span class="text-xs font-medium">Описание</span>
                </template>
                <template #body="{ data }">
                    <div v-if="data.description" class="flex flex-row gap-2 text-xs white-space-nowrap vertical-align-middle justify-content-start">
                        <span class="flex flex-row gap-2 text-xs white-space-nowrap vertical-align-middle justify-content-start">
                            <span class="flex my-auto" v-tooltip="{ value: data.description }">{{ getShortDescription(data.description) }}</span>
                        </span>
                        <Button @click="copy(data.description)" icon="pi pi-copy" iconPos="right" size="small" outlined severity="secondary" class="border-0" />
                    </div>
                </template>
            </Column>

            <Column field="transfer_id" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true">
                <template #header>
                    <span class="text-xs font-medium">TID</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
                </template>
                <template #body="{ data }">
                    <div v-if="data.data && data.data.order_id" class="flex flex-row gap-1 text-xs vertical-align-middle justify-content-start align-items-center">
                        <span class="flex my-auto overflow-hidden cursor-pointer" style="max-width:5em" @click="goTo(data.data.order_id, 'transfer')">
                            <span class="white-space-nowrap cursor-pointer text-primary" style="text-overflow:ellipsis;">
                                {{ data.data.order_id }}
                            </span>
                        </span>...
                        <div class="flex flex-row gap-1">
                            <Button @click="copy(data.data.order_id)" icon="pi pi-copy" iconPos="right" size="small" outlined severity="secondary" class="border-0" />
                            <Button @click="goTo(data.data.order_id, 'transfer', true)" icon="pi pi-external-link" iconPos="right" size="small" outlined severity="info" class="border-0" />
                        </div>
                    </div> 
                </template>
            </Column>

            <Column field="user_id" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true">
                <template #header>
                    <span class="text-xs font-medium">UID</span>
                    </template>
                    <template #filter="{ filterModel, filterCallback }">
                        <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
                    </template>
                <template #body="{ data }">
                    <div v-if="data.data && data.user_id" class="flex flex-row gap-1 text-xs vertical-align-middle justify-content-start align-items-center">
                        <span class="flex my-auto overflow-hidden cursor-pointer" style="max-width:5em" @click="goTo(data.user_id, 'user')">
                            <span class="white-space-nowrap cursor-pointer text-primary" style="text-overflow:ellipsis;">
                                {{ data.user_id }}
                            </span>
                        </span>...
                        <div class="flex flex-row gap-1">
                            <Button @click="copy(data.user_id)" icon="pi pi-copy" iconPos="right" size="small" outlined severity="secondary" class="border-0" />
                            <Button @click="goTo(data.user_id, 'user_wallet', true)" icon="pi pi-external-link" iconPos="right" size="small" outlined severity="info" class="border-0" />
                        </div>
                    </div> 
                </template>
            </Column>

            <Column field="error_message" header="" filterMatchMode="startsWith" >
                <template #header>
                    <span class="text-xs font-medium">Ошибка</span>
                </template>
                <template #body="{ data }">
                    <div v-if="data.error_message" class="flex flex-row gap-2 text-xs white-space-nowrap vertical-align-middle justify-content-start">
                        <span class="flex flex-row gap-2 text-xs white-space-nowrap vertical-align-middle justify-content-start">
                            <span class="flex my-auto" v-tooltip="{ value: data.error_message }">{{ getShortDescription(data.error_message) }}</span>
                        </span>
                        <Button @click="copy(data.error_message)" icon="pi pi-copy" iconPos="right" size="small" outlined severity="secondary" class="border-0" />
                    </div>
                </template>
            </Column>

            <Column field="hash" filterMatchMode="startsWith" :showFilterMatchModes="false" :showClearButton="true" :showApplyButton="true" >
                <template #header>
                    <span class="text-xs font-medium">hash</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
                </template>
                <template #body="{ data }">
                    <div v-if="data.hash" class="flex flex-row gap-1 text-sm vertical-align-middle justify-content-start align-items-center">
                        <span class="flex my-auto overflow-hidden bg-yellow-100" style="max-width:8em">
                            <span class="white-space-nowrap" style="text-overflow:ellipsis;">
                                {{ data.hash }}
                            </span>
                        </span>...
                        <Button @click="copy(data.hash)" icon="pi pi-copy" iconPos="right" size="small" outlined severity="secondary" class="border-0" />
                    </div> 
                </template>
            </Column>

            <Column field="actions">
                <template #header>
                    <span class="text-xs font-medium">Действия</span>
                </template>
                <template #body="{ data }">
                    <div class="flex gap-2">
                        <Button v-if="data.step === 'freezed'" label="Откат" icon="pi pi-replay" size="small" severity="danger" outlined @click="openRevertDialog(data)" :loading="loading" :disabled="loading" />
                        <Button v-if="data.step === 'risk'" label="Решить" icon="pi pi-shield" size="small" severity="warn" outlined @click="openRiskResolveDialog(data)" :loading="loading" :disabled="loading" />
                        <template v-if="data.status === 'pending' && data.step === 'approval_required'">
                            <Button label="Подтвердить" icon="pi pi-check" size="small" severity="success" outlined @click="openWithdrawalReviewDialog(data, 'approve')" :disabled="loading || withdrawalReviewSubmitting" />
                            <Button label="Отклонить" icon="pi pi-times" size="small" severity="danger" outlined @click="openWithdrawalReviewDialog(data, 'reject')" :disabled="loading || withdrawalReviewSubmitting" />
                        </template>
                    </div>
                </template>
            </Column>

            <Column field="retries" header="" filterMatchMode="startsWith" >
                <template #header>
                    <span class="text-xs font-medium">retries</span>
                </template>
                <template #body="{ data }">
                    <span class="flex text-sm">{{data.retries}}</span>
                </template>
            </Column>

            <Column field="external_source" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" :showApplyButton="false" > >
                <template #header>
                    <span class="text-xs font-medium">external_source</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <Dropdown v-model="filterModel.value" @change="filterCallback()" :options="sources" placeholder="Все" class="p-column-filter" :showClear="true">
                        <template #option="slotProps">
                            <Tag :severity="slotProps.option" :value="slotProps.option" class="text-xs"/>
                        </template>
                    </Dropdown>
                </template>
                <template #body="{ data }">
                    <span class="flex text-sm">{{data.external_source}}</span>
                </template>
            </Column>

            <Column field="created" header="" filterMatchMode="startsWith" >
                <template #header>
                    <span class="text-xs font-medium">Создан</span>
                </template>
                <template #body="{ data }">
                    <span class="flex text-xs">{{formatDate(data.created)}}</span>
                </template>
            </Column>

            <Column field="updated" header="" filterMatchMode="startsWith" >
                <template #header>
                    <span class="text-xs font-medium">Обновлен</span>
                </template>
                <template #body="{ data }">
                    <span class="flex text-xs">{{formatDate(data.updated)}}</span>
                </template>
            </Column>

            <template #footer>
                <span class="text-sm font-medium">
                    Всего {{ totalRecords }} операций
                </span>
            </template>
        </DataTable>
    </div>

    <Dialog class="flex flex-column border-none" modal
        v-model:visible="dialogTx"
        :position="screenSize=='xs' ? 'bottom' : 'center'"
        :style="(screenSize=='xs' || screenSize=='sm' || screenSize=='md') ? {width: '100%!important', height: '100%!important', margin: '0!important', padding: '0!important'} : { width: '100%!important' }" 
        :contentStyle="(screenSize=='xs' || screenSize=='sm' || screenSize=='md') ? { height: '100%' } : { height: 'auto' }"
        :class="(screenSize=='xs') ? 'h-full border-noround-bottom' : ''">

        <template #header>
            <div class="flex flex-column w-full mb-3">
                <span class="flex text-lg font-medium">Проводки</span>
            </div>
        </template>

        <div class="w-full mb-3">
            <OperationsTxList :items="txList" :currencies="currencies"/>
        </div>
    </Dialog>

    <Dialog class="flex flex-column border-none" modal
        v-model:visible="dialogRevert"
        :style="{ width: '30rem' }">

        <template #header>
            <div class="flex flex-column w-full mb-3">
                <span class="flex text-lg font-medium">Откат операции</span>
            </div>
        </template>

        <div class="flex flex-column gap-3 w-full">
            <span class="text-sm">ID: {{ revertOperationId }}</span>
            <div class="flex flex-column gap-2">
                <label class="text-sm font-medium">Причина отката</label>
                <InputText v-model="revertDescription" placeholder="Укажите причину" class="w-full" />
            </div>
        </div>

        <template #footer>
            <Button label="Отмена" severity="secondary" @click="dialogRevert = false" :loading="loading"/>
            <Button label="Откатить" severity="danger" icon="pi pi-replay" @click="confirmRevert" :disabled="!revertDescription || loading" :loading="loading" />
        </template>
    </Dialog>

    <Dialog class="flex flex-column border-none" modal
        v-model:visible="dialogRiskResolve"
        :style="{ width: '30rem' }">

        <template #header>
            <div class="flex flex-column w-full mb-3">
                <span class="flex text-lg font-medium">Решение по risk-операции</span>
            </div>
        </template>

        <div class="flex flex-column gap-3 w-full">
            <span class="text-sm">ID: {{ riskResolveOperationId }}</span>
            <div class="flex flex-column gap-2">
                <label class="text-sm font-medium">Решение</label>
                <SelectButton v-model="riskResolveDecision" :options="riskResolveOptions" optionLabel="label" optionValue="value" aria-labelledby="risk-resolve-decision" :allowEmpty="false" />
            </div>
            <div class="flex flex-column gap-2">
                <label class="text-sm font-medium">Комментарий</label>
                <Textarea v-model="riskResolveDescription" placeholder="Укажите комментарий" class="w-full" rows="4" />
            </div>
        </div>

        <template #footer>
            <Button label="Отмена" severity="secondary" @click="dialogRiskResolve = false" :loading="loading"/>
            <Button label="Отправить" icon="pi pi-send" @click="confirmRiskResolve" :disabled="!riskResolveDecision || !riskResolveDescription || loading" :loading="loading" />
        </template>
    </Dialog>

    <Dialog class="flex flex-column border-none" modal
        v-model:visible="dialogWithdrawalReview"
        :style="{ width: '30rem' }">

        <template #header>
            <div class="flex flex-column w-full mb-3">
                <span class="flex text-lg font-medium">{{ withdrawalReviewDecision === 'approve' ? 'Подтверждение операции' : 'Отклонение операции' }}</span>
            </div>
        </template>

        <div class="flex flex-column gap-3 w-full">
            <span class="text-sm">ID: {{ withdrawalReviewOperationId }}</span>
            <div class="flex flex-column gap-2">
                <label class="text-sm font-medium">Комментарий</label>
                <Textarea v-model="withdrawalReviewDescription" placeholder="Укажите причину" class="w-full" rows="4" />
            </div>
        </div>

        <template #footer>
            <Button label="Отмена" severity="secondary" @click="dialogWithdrawalReview = false" :disabled="withdrawalReviewSubmitting" />
            <Button :label="withdrawalReviewDecision === 'approve' ? 'Подтвердить' : 'Отклонить'" :icon="withdrawalReviewDecision === 'approve' ? 'pi pi-check' : 'pi pi-times'" :severity="withdrawalReviewDecision === 'approve' ? 'success' : 'danger'" @click="confirmWithdrawalReview" :disabled="!withdrawalReviewDescription.trim() || withdrawalReviewSubmitting" :loading="withdrawalReviewSubmitting" />
        </template>
    </Dialog>
</template>

<script>
import Connect from '@/assets/js/Connect.js'
import Helper from '@/assets/js/Helper.js'
import OperationsTxList from '@/components/admin/operation/op_items.vue'
import { FilterMatchMode, FilterOperator } from '@primevue/core/api'

export default {
    props: {
        operations: {
            type: Array,
            required: true
        },
        totalRecords: {
            type: Number,
            required: true
        },
        loading: {
            type: Boolean,
            required: true
        },
        defaultParams: {
            type: Object,
            required: true
        },
        filters: {
            type: Object,
            required: true
        }
    },
    components: {
        OperationsTxList
    },
    data() {
        return {
            currencies: [],
            currenciesCodes: [],
            dialogTx: false,
            txList: [],
            dialogRevert: false,
            revertOperationId: null,
            revertDescription: '',
            dialogRiskResolve: false,
            riskResolveOperationId: null,
            riskResolveDescription: '',
            riskResolveDecision: 'approve',
            riskResolveOptions: [
                { label: 'approve', value: 'approve' },
                { label: 'revert', value: 'revert' }
            ],
            dialogWithdrawalReview: false,
            withdrawalReviewOperationId: null,
            withdrawalReviewDecision: null,
            withdrawalReviewDescription: '',
            withdrawalReviewSubmitting: false,
            sort: {
                field: 'created_at',
                order: -1
            },
            lazyParams: this.defaultParams,
            filters: {
                '_id': { value: null, matchMode: 'contains' },
                'user_id': { value: null, matchMode: 'contains' },
                'status': { value: null, matchMode: 'contains' },
                'step': { value: null, matchMode: 'contains' },
                'type': { value: null, matchMode: 'contains' },
                'transfer_id': { value: null, matchMode: 'contains' },
                'external_source': { value: null, matchMode: 'contains' },
                'hash': { value: null, matchMode: 'contains' },
            },
            statuses: ['pending', 'processing', 'completed', 'success', 'failed'],
            steps: ['freezed', 'risk', 'reverted', 'reserved', 'processing', 'completed', 'success', 'failed'],
            types: [
               'virtual_withdraw',
                'onchain_withdraw',
                'virtual_deposit',
                'onchain_deposit',
                'virtual_transfer',
                'onchain_sweep',
                'virtual_multiple_transfer',
                'issuing_vcard',
                'topup_vcard',
                'squarefi_deposit',
                'payout_capitalist_bank'
            ],
            sources: ['internal', 'squarefi', 'another'],
            networks: [
                'bep20', 'trc20',
            ],
            cryptoCurrencies: [
                'usdt', 'usdt_trc'
            ]
        }
    },
    computed: {
        screenSize() { return this.$store.getters.StateScreenSize },
    },
    methods: {
        resetFilter(){
            this.lazyParams.filters = {
                '_id': { value: null, matchMode: 'contains' },
                'user_id': { value: null, matchMode: 'contains' },
                'status': { value: null, matchMode: 'contains' },
                'step': { value: null, matchMode: 'contains' },
                'type': { value: null, matchMode: 'contains' },
                'transfer_id': { value: null, matchMode: 'contains' },
                'external_source': { value: null, matchMode: 'contains' },
                'hash': { value: null, matchMode: 'contains' },
            }
            this.$emit('onFilter', this.lazyParams.filters)
        },
        getFormatAmount(value, currency, digits=2) {
            return this.helper.format_amount(value, currency, digits)
        },
        GetNetworkImage(val){
            return this.helper.GetNetworkImage(val)
        },
        GetCurrencyImage(val){
            if(!val || this.currencies.length === 0) return null
            return this.helper.GetCurrencyImage(val, this.currencies)
        },
        copy(text){
            this.helper.copy_clipboard(text)
            this.$toast.add({ severity: 'info', summary: 'Скопировано', detail: text, life: 1000 })
        },
        goToTransactions(id){
            if(!id) return
            window.open(`/admin/transactions/?operation_id=${id}`, '_blank')
        },
        goTo(id, type, new_tab = false) {
            if(!id) return
            if(!type) return
            if(new_tab){
                const routeData = this.$router.resolve({path: '/admin/'+type+'/' + id});
                window.open(routeData.href, '_blank');
            } else  this.$router.push({ path: '/admin/'+type+'/' + id })
        },
        goto_address(address, network='bep20'){
            if(!address || !network) return
            const prod = (process.env.VUE_APP_MODE == 'production') ? true : false
            const bsc = (prod) ? 'https://bscscan.com/address/' : 'https://testnet.bscscan.com/address/'
            const eth = (prod) ? 'https://etherscan.io/address/' : 'https://testnet.etherscan.io/address/'
            const trc = (prod) ? 'https://tronscan.org/#/address/' : 'https://nile.tronscan.org/#/address/'
            if(network == 'eth'){
                window.open(eth+address, '_blank');
            } else if(network == 'bep20'){
                window.open(bsc+address, '_blank');
            } else if(network == 'trc20'){
                window.open(trc+address, '_blank');
            }
        },
        goToHashBlock(hash, network){
            if(!hash || !network) return
            const test = (process.env.VUE_APP_MODE == 'production') ? false : true
            const url = this.helper.GetScanHashUrl(hash, network, test)
            if(!url) return
            window.open(url, '_blank');
        },
        getClassByLabel(label){
            return this.helper.GetClassByLabel(label)
        },
        getSysWallet(value){
            if(!value) return null
            if(value == this.wallet_id) return 'current'
            return this.helper.GetSystemWalletName(value, this.wallet_id, this.systemWallets)
        },
        getSysAccount(value){
            if(!value) return null
            if(value == this.account_id) return 'current'
            return this.helper.GetSystemAccountName(value, this.account_id, this.systemWallets)
        },
        getSeverityByType(type) {
            // if(type == 'onchain_sweep') return 'info'
            // if(type == 'onchain_deposit') return 'success'
            // if(type == 'onchain_withdraw') return 'warn'
            // if(type == 'issuing_vcard') return 'danger'
            return 'secondary'
        },
        getSeverityByStatus (status) {
            return this.helper.get_severity_from_status(status)
        },
        getShortDescription(value){
            if(!value) return ''
            return this.helper.get_short_name(value, 25)
        },
        getCurImage(code){
            return this.helper.GetCurrencyImage(code, this.currencies)
        },
        getNetworkImage(network) {
            if(!network) return null
           return this.helper.GetNetworkImage(network)
        },
        getStatusLabel(status) {
            return this.helper.get_severity_status(status, 'en')
        },
        getSeverity (status) {
            return this.helper.get_severity_from_status(status)
        },
        formatDate(value) {
            return this.helper.format_date(value)
        },
        getShortAddress(value){
            if(!value) return ''
            return this.helper.get_short_name(value)
        },
        getShortId(value){
            return this.helper.get_short_id(value)
        },
        showTx(items) {
            this.dialogTx = true
            this.txList = items ? items : []
        },
        onPage(event) {
            this.lazyParams = event
            this.$emit('onPageChange', this.lazyParams)
        },
        onSort(event) {
            if(event.sortField && event.sortOrder){
                this.sort = {
                    field: event.sortField,
                    order: event.sortOrder
                }
            } else {
                this.sort = {
                    field: 'created_at',
                    order: -1
                }
            }
        },
        onFilter() {
            this.$emit('onFilter', this.lazyParams.filters)
        },
        onRowSelect() {
            this.selectAll = this.selectedCustomers.length === this.totalRecords
        },
        onRowUnselect() {
            this.selectAll = false;
        },
        GetCurrencyList(){
            const objList = this.$store.state.payment.currencies
            if(!objList)return
            let currencies = []
            for(const [k,v] of Object.entries(objList)){
                currencies.push({
                    code:   v.code,
                    name:   v.name,
                    image:  v.image
                })
            }
            this.currencies = currencies
            this.currenciesCodes = Object.keys(objList)
        },
        openRevertDialog(data) {
            this.revertOperationId = data.id
            this.revertDescription = ''
            this.dialogRevert = true
        },
        openRiskResolveDialog(data) {
            this.riskResolveOperationId = data.id
            this.riskResolveDescription = ''
            this.riskResolveDecision = 'approve'
            this.dialogRiskResolve = true
        },
        openWithdrawalReviewDialog(data, decision) {
            if (data.status !== 'pending' || data.step !== 'approval_required') return
            this.withdrawalReviewOperationId = data.id
            this.withdrawalReviewDecision = decision
            this.withdrawalReviewDescription = ''
            this.dialogWithdrawalReview = true
        },
        async confirmWithdrawalReview() {
            const description = this.withdrawalReviewDescription.trim()
            if (!this.withdrawalReviewOperationId || !['approve', 'reject'].includes(this.withdrawalReviewDecision) || !description || this.withdrawalReviewSubmitting) return

            this.withdrawalReviewSubmitting = true
            try {
                const result = await this.connect.ReviewWithdrawalOperation(
                    this.withdrawalReviewOperationId,
                    this.withdrawalReviewDecision,
                    description
                )
                if (result && result.result) {
                    this.$toast.add({ severity: 'success', summary: 'Успешно', detail: 'Решение по операции отправлено', life: 3000 })
                    this.dialogWithdrawalReview = false
                    this.$emit('refresh')
                } else {
                    this.$toast.add({ severity: 'error', summary: 'Ошибка', detail: result && result.error ? result.error : 'Не удалось отправить решение', life: 3000 })
                }
            } catch (error) {
                console.error(error)
                this.$toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Произошла ошибка при отправке решения', life: 3000 })
            } finally {
                this.withdrawalReviewSubmitting = false
            }
        },
        async confirmRevert() {
            await this.RevertOperation(this.revertOperationId, this.revertDescription)
            this.dialogRevert = false
        },
        async confirmRiskResolve() {
            await this.ResolveRiskOperation(this.riskResolveOperationId, this.riskResolveDescription, this.riskResolveDecision)
            this.dialogRiskResolve = false
        },
        async RevertOperation(id, description) {
            try {
                const revert = await this.connect.RevertOperationById(id, description).then(res => res)
                console.log(revert)
                if (revert.result && revert.data) {
                    this.$toast.add({ severity: 'success', summary: 'Успешно', detail: 'Операция отправлена на отмену', life: 3000 })
                } else if(revert.result && revert.error){
                    this.$toast.add({ severity: 'error', summary: 'Ошибка', detail: revert.error, life: 3000 })
                } else {
                    this.$toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Неизвестная ошибка', life: 3000 })
                }
            } catch (error) {
                console.error(error)
                this.$toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Произошла ошибка при отправке операции на отмену', life: 3000 })
            }
        },
        async ResolveRiskOperation(id, description, decision) {
            try {
                const resolve = await this.connect.ResolveRiskOperationById(id, description, decision).then(res => res)
                console.log(resolve)
                if (resolve.result && resolve.data) {
                    this.$toast.add({ severity: 'success', summary: 'Успешно', detail: 'Решение по операции отправлено', life: 3000 })
                } else if(resolve.result && resolve.error){
                    this.$toast.add({ severity: 'error', summary: 'Ошибка', detail: resolve.error, life: 3000 })
                } else {
                    this.$toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Неизвестная ошибка', life: 3000 })
                }
                this.$emit('loading', false)
            } catch (error) {
                console.error(error)
                this.$toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Произошла ошибка при отправке решения', life: 3000 })
            }
        },
    },
    beforeCreate() {
        this.connect = new Connect()
        this.helper = new Helper()
    },
    mounted() {
        this.GetCurrencyList()
    }
}
</script>